import "./style.scss";
import { FC, useRef } from "react";
import { useEffect } from "react";
import ArrowSvg from "@/src/svg/autoCompile/ArrowSvg";
import TrashSvg from "@/src/svg/admissionOfficePage/TrashSvg";

function useClickOutside(ref: any, callback: () => void) {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [ref, callback]);
}

interface Props {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  onSelect: (v: string) => void;
  isOpen: boolean;
  options: string[];
  error?: boolean;
  required?: boolean;
  inputProps?: any;
  placeholder?: string;
  defaultValue?: string;
  identifier?: string;
}

const AutocompleteInput: FC<Props> = ({
  label,
  value,
  onChange,
  onSelect,
  isOpen,
  options,
  error,
  required,
  inputProps,
  placeholder,
  defaultValue,
  identifier,
}) => {
  const ref = useRef(null);

  useClickOutside(ref, () => {
    if (inputProps?.close) inputProps.close();
  });

  useEffect(() => {
    if (!value) {
      onChange(defaultValue || "");
      inputProps?.close?.();
    }
  }, []);

  // Keeps react-hook-form's internal state in sync with the controlled `value`.
  // Without this, typing or applying `defaultValue` updates only the local
  // autocomplete state, so RHF treats the field as empty and required-validation fails.
  useEffect(() => {
    const reg = inputProps?.register;
    if (reg?.onChange && reg?.name) {
      reg.onChange({
        target: { name: reg.name, value },
        type: "change",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleDelete = () => {
    onChange("");

    if (inputProps?.setValue && inputProps?.name) {
      inputProps.setValue(inputProps.name, "", {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (inputProps?.clearErrors && inputProps?.name) {
      inputProps.clearErrors(inputProps.name);
    }

    inputProps?.close?.();
  };

  return (
    <div className={`form-group-fields-input ${identifier ? identifier : ""}`} ref={ref}>
      {label && (
        <div className="form-group-fields-input-label">
          <label>
            {label}
            {required && <span>*</span>}
          </label>
          <div className="form-group-fields-input-label-line"></div>
        </div>
      )}
      <input
        {...inputProps.register}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => inputProps.setOpen?.(true)}
        onClick={() => inputProps.setOpen?.(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {identifier === "offered-course-auto" || identifier === "certificate" ? (
        <div className="form-group-fields-input-svg">
          <div className={`form-group-fields-input-svg-arrow ${isOpen && options.length > 0 ? "rotate" : ""}`}>
            <ArrowSvg />
          </div>
          <div className="form-group-fields-input-svg-line"></div>
          <div className="form-group-fields-input-svg-delete" onClick={handleDelete}>
            <TrashSvg />
          </div>
        </div>
      ) : (
        <div className={`form-group-fields-input-arrow ${isOpen && options.length > 0 ? "rotate" : ""}`}>
          <ArrowSvg />
        </div>
      )}

      {error && <p>This field is required</p>}

      {isOpen && options.length > 0 && (
        <ul className="autocomplete-list">
          {options.map((opt) => (
            <li key={opt} onClick={() => onSelect(opt)}>
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteInput;
